import { useEffect, useState } from "react";
import styled from "styled-components";
import { FiDownload, FiTrash2 } from "react-icons/fi";
import {
    AiOutlineFilePdf,
    AiOutlineFileImage,
    AiOutlineFileWord,
    AiOutlineFileExcel,
    AiOutlineFileUnknown,
} from "react-icons/ai";

import Overlay from "../../../Componentes/Overlay";
import Modal from "../../../Componentes/Utils/Modal";
import ViewContainer from "../../../Componentes/Utils/ViewContainer";
import SmallLoading from "../../../Componentes/Utils/SmallLoading";
import ObservacoesSection from "./ObservacoesSection";
import { formatCPF } from "../../../Funcoes/FormatCPF";
import { formatarData } from "../../../Funcoes/FormatarData";
import useApi from "../../../hooks/useApi";
import { useToast } from "../../../contexts/ToastContext";

/* ─── Layout geral ───────────────────────────────────────────────── */

const Titulo = styled.h3`
    margin: 0 0 18px 0;
    padding: 0 0 12px 2px;
    border-bottom: 1px solid #e5e7eb;
    font-size: 15px;
    font-weight: 700;
    color: #263b4b;
    letter-spacing: 0.02em;
    text-align: left;
    width: 100%;
    box-sizing: border-box;
`;

const Container = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
    box-sizing: border-box;
`;

const ErrorMsg = styled.p`
    margin: 0;
    font-size: 13px;
    color: #b91c1c;
    text-align: center;
`;

/* ─── Seção de anexos ────────────────────────────────────────────── */

const SecaoAnexos = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const AnexosLabel = styled.p`
    margin: 0 0 2px 2px;
    font-size: 12px;
    font-weight: 700;
    color: #263b4b;
    letter-spacing: 0.02em;
`;

const SemAnexos = styled.p`
    margin: 0;
    font-size: 13px;
    color: #6b7280;
    padding: 10px 12px;
    background-color: #f8fafc;
    border: 1px dashed #cbd5e1;
    border-radius: 8px;
    text-align: center;
`;

const CardAnexo = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    background-color: ${(p) => (p.$confirming ? "#fff5f5" : "#f8fafc")};
    border: 1px solid ${(p) => (p.$confirming ? "#fecaca" : "#e2e8f0")};
    border-radius: 10px;
    transition: border-color 0.15s ease, background-color 0.15s ease;
    min-width: 0;
`;

const AnexoIconWrap = styled.div`
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    background-color: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
`;

const AnexoInfo = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const AnexoNome = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const AnexoTamanho = styled.span`
    font-size: 11px;
    color: #64748b;
`;

const AnexoAcoes = styled.div`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 6px;
`;

const BotaoAnexo = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    color: ${(p) => (p.$danger ? "#b91c1c" : "#1d4ed8")};
    background-color: ${(p) =>
        p.$danger ? "rgba(185,28,28,0.10)" : "rgba(29,78,216,0.08)"};
    border: 1px solid transparent;

    &:hover {
        background-color: ${(p) =>
            p.$danger ? "rgba(185,28,28,0.20)" : "rgba(29,78,216,0.18)"};
        border-color: ${(p) =>
            p.$danger ? "rgba(185,28,28,0.45)" : "rgba(29,78,216,0.4)"};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    &:focus-visible {
        outline: 2px solid ${(p) => (p.$danger ? "#b91c1c" : "#1d4ed8")};
        outline-offset: 2px;
    }
`;

const ConfirmBox = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
`;

const ConfirmLabel = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: #b91c1c;
    white-space: nowrap;
`;

const BotaoConfirm = styled.button`
    all: unset;
    padding: 4px 10px;
    border-radius: 5px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.15s ease;

    background-color: ${(p) => (p.$cancel ? "#e5e7eb" : "#dc2626")};
    color: ${(p) => (p.$cancel ? "#333" : "#fff")};

    &:hover {
        background-color: ${(p) => (p.$cancel ? "#d1d5db" : "#b91c1c")};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

/* ─── Helpers ────────────────────────────────────────────────────── */

function formatarDataHora(value) {
    if (!value) return "-";
    try { return formatarData(value); } catch { return String(value); }
}

function formatarTempo(segundos) {
    if (segundos == null || segundos === "") return "-";
    const total = Number(segundos);
    if (!Number.isFinite(total)) return "-";
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return [h, m, s].map((n) => String(n).padStart(2, "0")).join(":");
}

function formatarCpf(value) {
    if (!value) return "-";
    const d = String(value).replace(/\D/g, "");
    return d.length === 11 ? formatCPF(d) : value;
}

function formatarMoeda(value) {
    if (value == null || value === "") return "-";
    const num = Number(value);
    if (!Number.isFinite(num)) return String(value);
    return num.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function displayValue(value) {
    if (value == null || value === "") return "-";
    return String(value);
}

function formatarTamanho(bytes) {
    if (bytes == null || bytes === "" || Number.isNaN(Number(bytes))) return null;
    const b = Number(bytes);
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function nomeArquivo(anexo) {
    if (anexo?.nome_original) return anexo.nome_original;
    const caminho = anexo?.caminho ?? "";
    const parts = String(caminho).split("/");
    return parts[parts.length - 1] || "Arquivo";
}

function getFileIcon(filename) {
    const name = String(filename ?? "").toLowerCase();
    if (/\.(png|jpe?g|gif|webp|bmp)$/i.test(name))
        return <AiOutlineFileImage size={20} color="#4a90e2" />;
    if (name.endsWith(".pdf"))
        return <AiOutlineFilePdf size={20} color="#e63946" />;
    if (/\.(doc|docx)$/i.test(name))
        return <AiOutlineFileWord size={20} color="#2a6db0" />;
    if (/\.(xls|xlsx)$/i.test(name))
        return <AiOutlineFileExcel size={20} color="#207245" />;
    return <AiOutlineFileUnknown size={20} color="#555" />;
}

/* ─── Componente ─────────────────────────────────────────────────── */

const ModalViewAtendimento = ({ atendimentoId, fechar }) => {
    const api = useApi();
    const { notifyError, notifySuccess } = useToast();

    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState(null);
    const [atendimento, setAtendimento] = useState(null);

    const [confirmDeleteAnexoId, setConfirmDeleteAnexoId] = useState(null);
    const [deletingAnexoId, setDeletingAnexoId] = useState(null);

    const carregarAtendimento = async (id) => {
        setLoading(true);
        setErro(null);
        setAtendimento(null);

        try {
            const { data } = await api.get("/Painel/API/AtendimentoAmb/Get/index.php", {
                params: { id },
            });

            if (!data?.success || !data?.data) {
                const msg = data?.message || "Atendimento não encontrado.";
                setErro(msg);
                notifyError(msg);
                return;
            }

            setAtendimento(data.data);
        } catch (err) {
            console.error(err);
            const msg = err?.response?.data?.message || "Erro ao carregar atendimento.";
            setErro(msg);
            notifyError(msg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!atendimentoId) return;
        carregarAtendimento(atendimentoId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [atendimentoId]);

    const handleDeleteAnexo = async (anexoId) => {
        setDeletingAnexoId(anexoId);
        try {
            const { data } = await api.post(
                "/Painel/API/AtendimentoAmb/DeleteAnexo/index.php",
                { id: anexoId }
            );

            if (data?.success) {
                notifySuccess(data?.message || "Anexo removido.");
                setAtendimento((prev) => ({
                    ...prev,
                    anexos: (prev?.anexos ?? []).filter((a) => a.id !== anexoId),
                }));
                setConfirmDeleteAnexoId(null);
            } else {
                notifyError(data?.message || "Não foi possível remover o anexo.");
            }
        } catch (err) {
            console.error(err);
            notifyError(err?.response?.data?.message || "Erro ao remover anexo.");
        } finally {
            setDeletingAnexoId(null);
        }
    };

    const anexos = Array.isArray(atendimento?.anexos) ? atendimento.anexos : [];
    const tipoAtendimento = atendimento?.atendimento;
    const showInternacao = tipoAtendimento === "EXTERNO";
    const showStatusInternacao = showInternacao && atendimento?.internacao === "SIM";

    return (
        <Overlay state>
            <Modal header close={fechar} size="md">
                <Titulo>VISUALIZAR ATENDIMENTO</Titulo>

                {loading && <SmallLoading text="Carregando atendimento…" />}

                {!loading && erro && <ErrorMsg>{erro}</ErrorMsg>}

                {!loading && !erro && atendimento && (
                    <Container>
                        <ViewContainer label="ID" valor={atendimento.id} />
                        <ViewContainer label="CPF" valor={formatarCpf(atendimento.cpf)} />
                        <ViewContainer label="NOME" valor={displayValue(atendimento.nome)} />
                        <ViewContainer label="MATRÍCULA" valor={displayValue(atendimento.matricula)} />
                        <ViewContainer label="FUNÇÃO" valor={displayValue(atendimento.funcao)} />
                        <ViewContainer
                            label="CENTRO DE CUSTO"
                            valor={displayValue(atendimento.centro_custo)}
                        />
                        <ViewContainer label="CONTRATO" valor={displayValue(atendimento.contrato)} />
                        <ViewContainer label="LÍDER" valor={displayValue(atendimento.lider)} />
                        <ViewContainer label="QUEIXA / OCORRÊNCIA" valor={displayValue(atendimento.queixa)} />
                        <ViewContainer label="TIPO ATENDIMENTO" valor={displayValue(tipoAtendimento)} />
                        {showInternacao && (
                            <ViewContainer
                                label="INTERNAÇÃO"
                                valor={displayValue(atendimento.internacao)}
                            />
                        )}
                        {showStatusInternacao && (
                            <ViewContainer
                                label="STATUS INTERNAÇÃO"
                                valor={displayValue(atendimento.obito_alta)}
                            />
                        )}
                        <ViewContainer label="DESCRIÇÃO" valor={displayValue(atendimento.descricao)} />
                        <ViewContainer label="SOLUÇÃO" valor={displayValue(atendimento.solucao)} />
                        <ViewContainer label="CUSTOS" valor={formatarMoeda(atendimento.custo)} />
                        <ViewContainer label="STATUS" valor={displayValue(atendimento.status)} />
                        <ViewContainer
                            label="DATA INÍCIO"
                            valor={formatarDataHora(atendimento.data_inicio)}
                        />
                        <ViewContainer
                            label="DATA FINAL"
                            valor={formatarDataHora(atendimento.data_final)}
                        />
                        <ViewContainer
                            label="TEMPO"
                            valor={formatarTempo(atendimento.segundos)}
                        />
                        <ViewContainer
                            label="PREENCHIDO POR"
                            valor={displayValue(atendimento.preenchido)}
                        />

                        <ObservacoesSection atendimentoId={atendimento.id} />

                        <SecaoAnexos>
                            <AnexosLabel>ANEXOS</AnexosLabel>

                            {anexos.length === 0 ? (
                                <SemAnexos>Nenhum anexo.</SemAnexos>
                            ) : (
                                anexos.map((anexo) => {
                                    const nome = nomeArquivo(anexo);
                                    const tamanho = formatarTamanho(anexo.tamanho);
                                    const isConfirming = confirmDeleteAnexoId === anexo.id;
                                    const isDeleting = deletingAnexoId === anexo.id;

                                    return (
                                        <CardAnexo
                                            key={anexo.id ?? anexo.caminho}
                                            $confirming={isConfirming}
                                        >
                                            <AnexoIconWrap>
                                                {getFileIcon(nome)}
                                            </AnexoIconWrap>

                                            <AnexoInfo>
                                                <AnexoNome title={nome}>{nome}</AnexoNome>
                                                {tamanho && (
                                                    <AnexoTamanho>{tamanho}</AnexoTamanho>
                                                )}
                                            </AnexoInfo>

                                            {isConfirming ? (
                                                <ConfirmBox>
                                                    <ConfirmLabel>Confirmar?</ConfirmLabel>
                                                    <BotaoConfirm
                                                        type="button"
                                                        $cancel
                                                        disabled={isDeleting}
                                                        onClick={() => setConfirmDeleteAnexoId(null)}
                                                    >
                                                        NÃO
                                                    </BotaoConfirm>
                                                    <BotaoConfirm
                                                        type="button"
                                                        disabled={isDeleting}
                                                        onClick={() => handleDeleteAnexo(anexo.id)}
                                                    >
                                                        {isDeleting ? "…" : "SIM"}
                                                    </BotaoConfirm>
                                                </ConfirmBox>
                                            ) : (
                                                <AnexoAcoes>
                                                    <BotaoAnexo
                                                        as="a"
                                                        href={anexo.caminho}
                                                        download
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        title="Baixar anexo"
                                                    >
                                                        <FiDownload size={14} />
                                                    </BotaoAnexo>
                                                    <BotaoAnexo
                                                        $danger
                                                        type="button"
                                                        title="Remover anexo"
                                                        onClick={() =>
                                                            setConfirmDeleteAnexoId(anexo.id)
                                                        }
                                                    >
                                                        <FiTrash2 size={14} />
                                                    </BotaoAnexo>
                                                </AnexoAcoes>
                                            )}
                                        </CardAnexo>
                                    );
                                })
                            )}
                        </SecaoAnexos>
                    </Container>
                )}
            </Modal>
        </Overlay>
    );
};

export default ModalViewAtendimento;
